import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { authFetch } from '../common/authFetch';
import { useAuth } from '../common/AuthContext';

const StreakContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const StreakProvider = ({ children }) => {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();
  const [streak, setStreak] = useState(null);
  const [badges, setBadges] = useState([]);
  const [streakLoading, setStreakLoading] = useState(false);

  const fetchStreak = useCallback(async () => {
    try {
      setStreakLoading(true);
      const response = await authFetch(`${API_URL}/api/v1/streak`);
      const data = await response.json();
      if (data.success) {
        setStreak(data.streak || null);
      }
    } catch (error) {
      console.error('Error fetching streak:', error);
    } finally {
      setStreakLoading(false);
    }
  }, []);

  const fetchBadges = useCallback(async () => {
    try {
      setStreakLoading(true);
      const response = await authFetch(`${API_URL}/api/v1/streak/badges`);
      const data = await response.json();
      if (data.success) {
        setBadges(data.badges || []);
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
    } finally {
      setStreakLoading(false);
    }
  }, []);

  const checkStreak = useCallback(async () => {
    try {
      const response = await authFetch(`${API_URL}/api/v1/streak/check`, {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        // Update streak and badges after check
        await fetchStreak();
        await fetchBadges();

        // Show toast for new badges
        if (data.new_badges && data.new_badges.length > 0) {
          data.new_badges.forEach((badge) => {
            toast.success(`${t('streak.newBadge')} ${badge.badge_key}`, {
              position: 'top-right',
              autoClose: 5000
            });
          });
        }
      }
    } catch (error) {
      console.error('Error checking streak:', error);
    }
  }, [t, fetchStreak, fetchBadges]);

  // Auto-fetch on mount when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchStreak();
      fetchBadges();
    }
  }, [isAuthenticated, fetchStreak, fetchBadges]);

  const value = {
    streak,
    badges,
    streakLoading,
    fetchStreak,
    fetchBadges,
    checkStreak
  };

  return (
    <StreakContext.Provider value={value}>
      {children}
    </StreakContext.Provider>
  );
};

export const useStreak = () => {
  const context = useContext(StreakContext);
  if (!context) {
    throw new Error('useStreak must be used within a StreakProvider');
  }
  return context;
};
